import React from 'react';
import { Drawer, Form, Button, SelectPicker, Message } from 'rsuite';
import { useTranslation } from 'react-i18next';

interface User {
  _id: string;
  firstname: string;
  lastname: string;
  registrationNumber: string;
}

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    registrationNumber: string;
    status: string[];
    type?: string;
  };
  users: User[];
  usersLoading: boolean;
  toaster: any;
  type?: string;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  usersLoading,
  toaster,
  type = ''
}) => {
  const [formValue, setFormValue] = React.useState({ ...currentFilters, type: '' });
  const { t } = useTranslation();

  const statusOptions = [
    { label: 'Pending', value: 'pending' },
    { label: 'Processing', value: 'processing' },
    { label: 'Closed', value: 'closed' }
  ];

  const typeOptions = [
    { label: t('Home Office'), value: 'remoteWork' },
    { label: t('Third Party Site'), value: 'thirdPartySite' }
  ];

  const userOptions = React.useMemo(() => {
    return users?.map(user => ({
      label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
      value: user._id
    }));
  }, [users]);

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => {
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== '' && value !== null;
    });

    if (hasFilters) {
      onFiltersChange({
        userId: formValue.userId,
        registrationNumber: formValue.registrationNumber?.toLowerCase(),
        status: formValue.status,
        type: formValue.type
      });
    } else {
      onResetFilters();
    }

    onClose();
  };

  const handleReset = () => {
    setFormValue({
      userId: '',
      registrationNumber: '',
      status: [],
      type: ''
    });
    onResetFilters();
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      setFormValue({ ...currentFilters, type: currentFilters.type || '' });
    }
  }, [open, currentFilters]);

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClose={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User & Registration Number')}</Form.ControlLabel>
            <Form.Control
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select user')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Status')}</Form.ControlLabel>
            <Form.Control
              name="status"
              accepter={SelectPicker}
              data={statusOptions}
              searchable={false}
              placeholder={t('Select status')}
              block
              multiple
            />
          </Form.Group>

          {type === 'Combined' && (
            <Form.Group>
              <Form.ControlLabel>{t('Type')}</Form.ControlLabel>
              <Form.Control
                name="type"
                accepter={SelectPicker}
                data={typeOptions}
                searchable={false}
                placeholder={t('Select type')}
                block
              />
            </Form.Group>
          )}
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
